/**
 * The whole app. A handful of pages, so the address is read by hand rather than through a
 * router:
 *
 *   /              search
 *   /card/123      a card, its market value and the evaluator
 *   /history       evaluations, newest first
 *   /history/45    one evaluation in full
 *   /settings      evaluator settings and data updates
 *
 * On a phone the pages are tabs along the bottom, where a thumb reaches; on a wider
 * screen they sit in the top bar. Each page opened is pushed onto the browser history, so
 * the back button works, and the search stays mounted underneath so going back finds the
 * same results.
 */

import { type ReactNode, useCallback, useEffect, useState } from "react";
import { History as HistoryIcon, Search as SearchIcon, SlidersHorizontal } from "lucide-react";
import { api, ApiError, type User } from "./api";
import { CardPage } from "./CardPage";
import { History, HistoryEntry } from "./History";
import { Login } from "./Login";
import { Search } from "./Search";
import { SettingsPage } from "./SettingsPage";
import { cx } from "./ui";

type Route =
  | { page: "search" }
  | { page: "card"; id: number }
  | { page: "history" }
  | { page: "evaluation"; id: number }
  | { page: "settings" };

type Tab = "search" | "history" | "settings";

function routeFromPath(path = window.location.pathname): Route {
  let m = /^\/card\/(\d+)$/.exec(path);
  if (m) return { page: "card", id: Number(m[1]) };
  m = /^\/history\/(\d+)$/.exec(path);
  if (m) return { page: "evaluation", id: Number(m[1]) };
  if (path === "/history") return { page: "history" };
  if (path === "/settings") return { page: "settings" };
  return { page: "search" };
}

function pathOf(route: Route): string {
  switch (route.page) {
    case "card": return `/card/${route.id}`;
    case "evaluation": return `/history/${route.id}`;
    case "history": return "/history";
    case "settings": return "/settings";
    default: return "/";
  }
}

function tabOf(route: Route): Tab {
  if (route.page === "history" || route.page === "evaluation") return "history";
  if (route.page === "settings") return "settings";
  return "search";
}

const TABS: Array<{ tab: Tab; label: string; icon: ReactNode; route: Route }> = [
  { tab: "search", label: "Search", icon: <SearchIcon className="h-5 w-5" />, route: { page: "search" } },
  { tab: "history", label: "History", icon: <HistoryIcon className="h-5 w-5" />, route: { page: "history" } },
  { tab: "settings", label: "Settings", icon: <SlidersHorizontal className="h-5 w-5" />, route: { page: "settings" } },
];

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [route, setRoute] = useState<Route>(routeFromPath());

  useEffect(() => {
    api.me().then((r) => setUser(r.user)).catch(() => setUser(null));
    const onPop = () => setRoute(routeFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((next: Route) => {
    window.history.pushState(null, "", pathOf(next));
    setRoute(next);
    window.scrollTo(0, 0);
  }, []);

  const back = useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else go({ page: "search" });
  }, [go]);

  /** A call that comes back 401 means the session ended: show the login again. */
  const onError = useCallback((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) setUser(null);
  }, []);

  if (user === undefined) return <div className="min-h-screen bg-navy-900" />;
  if (user === null) return <Login onLogin={setUser} />;

  const active = tabOf(route);

  return (
    <div className="min-h-screen pb-20 md:pb-10">
      <header className="sticky top-0 z-30 bg-navy-900 text-white shadow-[0_1px_0_rgba(255,255,255,0.06)]">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
          <button type="button" onClick={() => go({ page: "search" })} className="flex items-center gap-2.5 text-left" aria-label="OHC Price Check, search">
            <img src="/brand/emblem.png" alt="" className="h-9 w-9" />
            <span className="leading-tight">
              <span className="block text-[15px] font-semibold tracking-tight">Price Check</span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-gold-400">Oh-Ho Collectibles</span>
            </span>
          </button>
          <nav className="ml-auto hidden gap-1 md:flex">
            {TABS.map((t) => (
              <button
                key={t.tab}
                type="button"
                onClick={() => go(t.route)}
                className={cx(
                  "flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition",
                  active === t.tab ? "bg-white/10 text-white" : "text-navy-200 hover:text-white",
                )}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl">
        <div className={route.page === "search" ? "" : "hidden"}>
          <Search onOpen={(id) => go({ page: "card", id })} onError={onError} />
        </div>
        {route.page === "card" && <CardPage id={route.id} onOpen={(id) => go({ page: "card", id })} onBack={back} onError={onError} />}
        {route.page === "history" && <History onOpen={(id) => go({ page: "evaluation", id })} onError={onError} />}
        {route.page === "evaluation" && <HistoryEntry id={route.id} onBack={back} onCard={(id) => go({ page: "card", id })} onError={onError} />}
        {route.page === "settings" && (
          <SettingsPage user={user} onError={onError} onLogout={() => api.logout().finally(() => setUser(null))} />
        )}
      </main>

      <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur md:hidden">
        <div className="mx-auto grid max-w-md grid-cols-3">
          {TABS.map((t) => (
            <button
              key={t.tab}
              type="button"
              onClick={() => go(t.route)}
              className={cx(
                "flex flex-col items-center gap-0.5 pb-2 pt-2.5 text-[11px] font-medium transition",
                active === t.tab ? "text-navy-900" : "text-slate-400",
              )}
            >
              <span className={cx("rounded-full px-4 py-0.5 transition", active === t.tab && "bg-gold-100")}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
